import React, { useState, useEffect } from 'react';
import { initializeApp } from 'firebase/app';
import { getAuth, signInAnonymously, signInWithCustomToken, onAuthStateChanged, GoogleAuthProvider, signInWithPopup, signOut } from 'firebase/auth';
import { getFirestore, doc, setDoc, getDoc, collection, getDocs, deleteDoc, onSnapshot } from 'firebase/firestore';

const __app_id = 'mistfall-app';
const __initial_auth_token = ''; // Leave this as an empty string for local anonymous sign-in or Google Sign-in.
// --- End of added lines ---

// Main App component for the character sheet
const App = () => {
  // Firebase states
  const [db, setDb] = useState(null);
  const [auth, setAuth] = useState(null);
  const [userId, setUserId] = useState(null);
  const [isAuthReady, setIsAuthReady] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');
  const [isLoadingData, setIsLoadingData] = useState(true);
  const [userEmail, setUserEmail] = useState(null); // To display Google user's email

  // Character Data States
  const [characterName, setCharacterName] = useState(''); // Name of the currently loaded character
  const [activeCharacterId, setActiveCharacterId] = useState(''); // ID (name) of the currently loaded character
  const [characterList, setCharacterList] = useState([]); // List of {id, name} for all saved characters

  const [heritage, setHeritage] = useState(''); // Stores the selected heritage key
  const [lifestyle, setLifestyle] = useState(''); // New state for lifestyle
  const [background, setBackground] = useState(''); // New state for background
  const [characterClass, setCharacterClass] = useState(''); // Stores the selected class key
  const [motivation, setMotivation] = useState(''); // New state for Motivation
  const [hinderance, setHinderance] = useState(''); // New state for Hinderance

  const [characteristics, setCharacteristics] = useState({
    physicality: { score: 2, status: 0, defensePool: 0 },
    dexterity: { score: 2, status: 0, defensePool: 0 },
    mentality: { score: 2, status: 0, defensePool: 0 },
    soul: { score: 2, status: 0, defensePool: 0 },
    influence: { score: 2, status: 0, defensePool: 0 },
    affluence: { score: 2, status: 0, defensePool: 0 },
    edge: { score: 2 }, // Edge does not have a status pool or defense pool
  });

  const [skills, setSkills] = useState({
    // Physicality Skills
    brawling: 0, melee: 0, withstand: 0, athletics: 0, endurance: 0, resilience: 0, 'lore: martial': 0,
    // Dexterity Skills
    stealth: 0, marksmanship: 0, thievery: 0, driving: 0, dodge: 0, acrobatics: 0, 'lore: survival': 0,
    // Mentality Skills
    tracking: 0, education: 0, research: 0, cunning: 0, concentration: 0, investigation: 0, 'lore: scholar': 0,
    // Soul Skills
    willpower: 0, discipline: 0, occult: 0, religion: 0, perception: 0, observation: 0, 'lore: forbidden': 0,
    // Influence Skills
    charm: 0, deception: 0, leadership: 0, intimidation: 0, manipulation: 0, 'lore: social': 0,
    // Affluence Skills
    negotiation: 0, finance: 0, assessment: 0, 'street wise': 0, bribe: 0, purchase: 0, 'lore: business': 0,
  });

  // Nemesis states
  const [cyclesCompleted, setCyclesCompleted] = useState(0);
  const [nemeses, setNemeses] = useState([]);

  // New states for Equipment, Locations, Traits, Talents
  const [ownedEquipment, setOwnedEquipment] = useState([]);
  const [ownedLocations, setOwnedLocations] = useState([]);
  const [ownedTraits, setOwnedTraits] = useState([]);
  const [ownedTalents, setOwnedTalents] = useState([]);

  // New states for Currency Trackers
  const [clues, setClues] = useState(0);
  const [leads, setLeads] = useState(0);
  const [acumen, setAcumen] = useState(0);
  const [essence, setEssence] = useState(0);

  // New states for Bonds
  const [npcBonds, setNpcBonds] = useState([]);
  const [pcBonds, setPcBonds] = useState([]);

  // State to temporarily hold new tag input for each item
  const [newQualityTagInput, setNewQualityTagInput] = useState({});
  const [newMagicalTagInput, setNewMagicalTagInput] = useState({});

  // Define static data (moved here to be defined after state hooks)
  const heritageData = {
    '': { label: 'Select Heritage', bonuses: {} }, // Default empty option
    'Pure-blooded Human': { label: 'Pure-blooded Human', bonuses: {} }, // All 2s, no specific bonus
    'Nephilim': { label: 'Nephilim', bonuses: { soul: 1 } }, // Soul +1
    'Cambion': { label: 'Cambion', bonuses: {} }, // All 2s, no specific bonus
    'Awakened': { label: 'Awakened', bonuses: { edge: 1 } }, // Edge +1
    'Fey Hybrid': { label: 'Fey Hybrid', bonuses: { influence: 1 } }, // Influence +1
    'Gnome': { label: 'Gnome', bonuses: { affluence: 1 } }, // Affluence +1
    'Fomorian': { label: 'Fomorian', bonuses: { physicality: 1 } }, // Physicality +1
    'Dweorg': { label: 'Dweorg', bonuses: { physicality: 1 } }, // Physicality +1
  };

  const lifestyleData = {
    '': { label: 'Select Lifestyle', bonuses: {} },
    'Average': { label: 'Average', bonuses: {} }, // No direct characteristic bonus, gains a trait
    'Athlete': { label: 'Athlete', bonuses: {} }, // Can increase Dex or Phy by 1 (player choice, handled manually for now)
    'Mindful': { label: 'Mindful', bonuses: { mentality: 1 } },
    'Brute': { label: 'Brute', bonuses: { physicality: 2 } },
    'Savvy': { label: 'Savvy', bonuses: { affluence: 1 } },
    'Spiritual': { label: 'Spiritual', bonuses: { soul: 1 } },
    'Charismatic': { label: 'Charismatic', bonuses: { influence: 1 } },
  };

  const backgroundData = {
    '': { label: 'Select Background', bonuses: {} },
    'Family': { label: 'Family', bonuses: {} },
    'Vagabond': { label: 'Vagabond', bonuses: {} },
    'Educated': { label: 'Educated', bonuses: {} },
    'Street-Urchin': { label: 'Street-Urchin', bonuses: {} },
    'Mystic': { label: 'Mystic', bonuses: {} },
    'Recluse': { label: 'Recluse', bonuses: {} },
    'Traditional': { label: 'Traditional', bonuses: {} },
    'Unconventional': { label: 'Unconventional', bonuses: {} },
  };

  const classData = {
    '': { label: 'Select Class' },
    'Druid': { label: 'Druid' },
    'Wizard': { label: 'Wizard' },
    'Witch': { label: 'Witch' },
    'Shaman': { label: 'Shaman' },
    'Warlock': { label: 'Warlock' },
    'Voodoo Priest of Lwa': { label: 'Voodoo Priest of Lwa' },
    'Vessel': { label: 'Vessel' },
  };

  const motivationData = {
    '': { label: 'Select Motivation' },
    'Justice': { label: 'Justice' },
    'Revenge': { label: 'Revenge' },
    'Power': { label: 'Power' },
    'Knowledge': { label: 'Knowledge' },
    'Redemption': { label: 'Redemption' },
    'Survival': { label: 'Survival' },
    'Love': { label: 'Love' },
    'Greed': { label: 'Greed' },
    'Fame': { label: 'Fame' },
    'Protection': { label: 'Protection' },
  };

  const hinderanceData = {
    '': { label: 'Select Hinderance' },
    'Cowardice': { label: 'Cowardice' },
    'Arrogance': { label: 'Arrogance' },
    'Addiction': { label: 'Addiction' },
    'Naivety': { label: 'Naivety' },
    'Blind Loyalty': { label: 'Blind Loyalty' },
    'Hot-Headed': { label: 'Hot-Headed' },
    'Curiosity': { label: 'Curiosity' },
    'Amnesia': { label: 'Amnesia' },
    'Physical Disability': { label: 'Physical Disability' },
    'Wanted': { label: 'Wanted' },
  };

  // Removed allTalentsData as it's no longer used for auto-filling
  const allTalentsData = { /* No longer used for auto-filling */ };

  const skillCategories = {
    physicality: ['brawling', 'melee', 'withstand', 'athletics', 'endurance', 'resilience', 'lore: martial'],
    dexterity: ['stealth', 'marksmanship', 'thievery', 'driving', 'dodge', 'acrobatics', 'lore: survival'],
    mentality: ['tracking', 'education', 'research', 'cunning', 'concentration', 'investigation', 'lore: scholar'],
    soul: ['willpower', 'discipline', 'occult', 'religion', 'perception', 'observation', 'lore: forbidden'],
    influence: ['charm', 'deception', 'leadership', 'intimidation', 'manipulation', 'lore: social'],
    affluence: ['negotiation', 'finance', 'assessment', 'street wise', 'bribe', 'purchase', 'lore: business'],
  };

  // Define common inventory slots
  const inventorySlots = [
    { value: '', label: 'Select Slot' },
    { value: 'attire', label: 'Attire' },
    { value: 'weapon-1', label: 'Weapon 1' },
    { value: 'weapon-2', label: 'Weapon 2' },
    { value: 'storage-slot-1', label: 'Storage Slot 1' },
    { value: 'storage-slot-2', label: 'Storage Slot 2' },
    { value: 'storage-slot-3', label: 'Storage Slot 3' },
    { value: 'accessory-1', label: 'Accessory 1' },
    { value: 'accessory-2', label: 'Accessory 2' },
    { value: 'accessory-3', label: 'Accessory 3' },
    { value: 'jewelry-1', label: 'Jewelry 1' },
    { value: 'jewelry-2', label: 'Jewelry 2' },
    { value: 'jewelry-3', label: 'Jewelry 3' },
    { value: 'vehicle-mount', label: 'Vehicle/Mount' },
  ];

  // Nemesis Data for Generation (moved here for clarity)
  const nemesisBaseStats = {
    physicality: 2, dexterity: 2, mentality: 2, soul: 2, influence: 2, affluence: 2, edge: 1
  };

  const nemesisChallengeLevels = [
    { value: 'Easy', label: 'Easy', boost: 1 },
    { value: 'Challenging', label: 'Challenging', boost: 2 },
    { value: 'Epic', label: 'Epic', boost: 3 },
  ];

  const nemesisCharacteristicTypes = [
    { value: 'physicality', label: 'Physicality' },
    { value: 'dexterity', label: 'Dexterity' },
    { value: 'mentality', label: 'Mentality' },
    { value: 'soul', label: 'Soul' },
    { value: 'influence', label: 'Influence' },
    { value: 'affluence', label: 'Affluence' },
  ];

  const fateValues = ['F1', 'F2', 'F3', 'F4', 'F5', 'F6', 'F7'];


  // --- Firebase Initialization and Authentication ---
  useEffect(() => {
    try {
      const firebaseConfig = {
  apiKey: "AIzaSyAhUkIJMdz3V-K0tgWkgg3muom7axu2rhY",
  authDomain: "mistfall-ffe0c.firebaseapp.com",
  databaseURL: "https://mistfall-ffe0c-default-rtdb.firebaseio.com",
  projectId: "mistfall-ffe0c",
  storageBucket: "mistfall-ffe0c.firebasestorage.app",
  messagingSenderId: "986674237447",
  appId: "1:986674237447:web:c58205ecf8894166879aa2",
  measurementId: "G-276NN7MV4X"
};

      const app = initializeApp(firebaseConfig);
      const firestoreDb = getFirestore(app);
      const firebaseAuth = getAuth(app);

      setDb(firestoreDb);
      setAuth(firebaseAuth);

      const unsubscribe = onAuthStateChanged(firebaseAuth, async (user) => {
        if (user) {
          setUserId(user.uid);
          setUserEmail(user.email); // Set email if available (for Google users)
        } else {
          // If no user is authenticated, try to sign in anonymously
          try {
            // Use the global __initial_auth_token provided by the Canvas environment
            // When deploying locally, __initial_auth_token would be an empty string as defined at the top
            if (typeof __initial_auth_token !== 'undefined' && __initial_auth_token !== '') {
              await signInWithCustomToken(firebaseAuth, __initial_auth_token);
            } else {
              await signInAnonymously(firebaseAuth);
            }
          } catch (error) {
            console.error("Error signing in anonymously:", error);
            setUserId(crypto.randomUUID()); // Fallback to a random ID if auth fails
          }
        }
        setIsAuthReady(true); // Auth state is ready
      });

      return () => unsubscribe(); // Cleanup auth listener
    } catch (error) {
      console.error("Failed to initialize Firebase:", error);
      setUserId(crypto.randomUUID()); // Fallback to a random ID if Firebase init fails
      setIsAuthReady(true);
    }
  }, []);

  // --- Load Character Data from Firestore ---
  // Defined as a function declaration to be hoisted
  function loadCharacterData(charId) {
    setIsLoadingData(true);
    // Only attempt to load if a valid charId is provided
    if (!charId || !db || !userId) {
        resetCharacterSheet(); // Reset if no valid ID
        setIsLoadingData(false);
        return;
    }

    const appId = typeof __app_id !== 'undefined' ? __app_id : 'default-app-id';
    const docRef = doc(db, `artifacts/${appId}/users/${userId}/characterSheets`, charId);

    const unsubscribe = onSnapshot(docRef, (docSnap) => {
      if (docSnap.exists()) {
        const data = docSnap.data();
        setCharacterName(data.characterName || charId); // Use activeCharacterId as fallback name
        setHeritage(data.heritage || '');
        setLifestyle(data.lifestyle || '');
        setBackground(data.background || '');
        setCharacterClass(data.characterClass || '');
        setMotivation(data.motivation || ''); // Load Motivation
        setHinderance(data.hinderance || ''); // Load Hinderance
        setCharacteristics(data.characteristics || {
          physicality: { score: 2, status: 0, defensePool: 0 }, dexterity: { score: 2, status: 0, defensePool: 0 },
          mentality: { score: 2, status: 0, defensePool: 0 }, soul: { score: 2, status: 0, defensePool: 0 },
          influence: { score: 2, status: 0, defensePool: 0 }, affluence: { score: 2, status: 0, defensePool: 0 },
          edge: { score: 2 },
        });
        setSkills(data.skills || {
          brawling: 0, melee: 0, withstand: 0, athletics: 0, endurance: 0, resilience: 0, 'lore: martial': 0,
          stealth: 0, marksmanship: 0, thievery: 0, driving: 0, dodge: 0, acrobatics: 0, 'lore: survival': 0,
          tracking: 0, education: 0, research: 0, cunning: 0, concentration: 0, investigation: 0, 'lore: scholar': 0,
          willpower: 0, discipline: 0, occult: 0, religion: 0, perception: 0, observation: 0, 'lore: forbidden': 0,
          charm: 0, deception: 0, leadership: 0, intimidation: 0, manipulation: 0, 'lore: social': 0,
          negotiation: 0, finance: 0, assessment: 0, 'street wise': 0, bribe: 0, purchase: 0, 'lore: business': 0,
        });
        // Ensure grantedWildcards is an array when loading and initialize it if needed
        const loadedEquipment = data.ownedEquipment || [];
        setOwnedEquipment(loadedEquipment.map(item => ({
            ...item,
            grantedWildcards: Array.from({ length: item.inventoryWildcardsGranted || 0 }, (_, i) => ({ id: item.grantedWildcards?.[i]?.id || Date.now() + i, text: item.grantedWildcards?.[i]?.text || '', used: item.grantedWildcards?.[i]?.used || false }))
        })) || []);
        setOwnedLocations(data.ownedLocations || []);
        setOwnedTraits(data.ownedTraits || []);
        setOwnedTalents(data.ownedTalents || []);
        setNpcBonds(data.npcBonds || []); // Load NPC Bonds
        setPcBonds(data.pcBonds || []);   // Load PC Bonds
        setCyclesCompleted(data.cyclesCompleted || 0); // Load Cycles Completed
        setNemeses(data.nemeses?.map(nemesis => {
            const loadedNemesis = { ...nemesis };
            // Ensure characteristics sub-objects have base, modifier, total, and fate
            const defaultNemesisChars = {};
            for (const charKey in nemesisBaseStats) {
                defaultNemesisChars[charKey] = { base: nemesisBaseStats[charKey], modifier: 0, total: nemesisBaseStats[charKey], fate: 'F1' };
            }
            loadedNemesis.characteristics = loadedNemesis.characteristics || {};
            for (const charKey in defaultNemesisChars) {
                loadedNemesis.characteristics[charKey] = {
                    ...defaultNemesisChars[charKey],
                    ...loadedNemesis.characteristics[charKey],
                    total: (loadedNemesis.characteristics[charKey]?.base || defaultNemesisChars[charKey].base) + (loadedNemesis.characteristics[charKey]?.modifier || 0),
                    fate: loadedNemesis.characteristics[charKey]?.fate || 'F1'
                };
            }
            return loadedNemesis;
          }) || []);
        setClues(data.clues || 0);
        setLeads(data.leads || 0);
        setAcumen(data.acumen || 0);
        setEssence(data.essence || 0);
      } else {
        console.log(`Document for character ID ${activeCharacterId} does not exist. Resetting.`);
        resetCharacterSheet();
      }
      setIsLoadingData(false);
    }, (error) => {
      console.error("Error fetching document:", error);
      setSaveMessage("Error loading character data.");
      setIsLoadingData(false);
    });

    return () => unsubscribe();
  }

  // --- Load Character List and Initial Character Data from Firestore ---
  useEffect(() => {
    if (db && userId && isAuthReady) {
      const appId = typeof __app_id !== 'undefined' ? __app_id : 'default-app-id';
      const charactersCollectionRef = collection(db, `artifacts/${appId}/users/${userId}/characterSheets`);

      const fetchCharacterList = async () => {
        setIsLoadingData(true);
        try {
          const querySnapshot = await getDocs(charactersCollectionRef);
          const characters = [];
          querySnapshot.forEach((doc) => {
            characters.push({ id: doc.id, name: doc.data().characterName || doc.id });
          });
          setCharacterList(characters);

          if (characters.length > 0) {
            const firstCharacterId = characters[0].id;
            setActiveCharacterId(firstCharacterId);
            // loadCharacterData is called by the activeCharacterId useEffect
          } else {
            // If no characters, set a default "New Character" option
            setActiveCharacterId(''); // Ensure no character is active initially
            setCharacterName('New Character'); // Set default display name
            resetCharacterSheet(); // Reset all fields
          }
        } catch (error) {
          console.error("Error fetching character list:", error);
          setSaveMessage("Error loading character list.");
          resetCharacterSheet();
        } finally {
          setIsLoadingData(false);
        }
      };

      fetchCharacterList();

      const unsubscribeList = onSnapshot(charactersCollectionRef, (snapshot) => {
        const updatedCharacters = [];
        snapshot.forEach((doc) => {
          updatedCharacters.push({ id: doc.id, name: doc.data().characterName || doc.id });
        });
        setCharacterList(updatedCharacters);
      }, (error) => {
        console.error("Error listening to character list changes:", error);
      });

      return () => unsubscribeList();
    }
  }, [db, userId, isAuthReady]); // Re-run when auth state changes


  // Defined as a function declaration to be hoisted
  function resetCharacterSheet() {
    setCharacterName('');
    setHeritage('');
    setLifestyle('');
    setBackground('');
    setCharacterClass('');
    setMotivation(''); // Reset Motivation
    setHinderance(''); // Reset Hinderance
    setCharacteristics({
      physicality: { score: 2, status: 0, defensePool: 0 },
      dexterity: { score: 2, status: 0, defensePool: 0 },
      mentality: { score: 2, status: 0, defensePool: 0 },
      soul: { score: 2, status: 0, defensePool: 0 },
      influence: { score: 2, status: 0, defensePool: 0 },
      affluence: { score: 2, status: 0, defensePool: 0 },
      edge: { score: 2 },
    });
    setSkills({
      brawling: 0, melee: 0, withstand: 0, athletics: 0, endurance: 0, resilience: 0, 'lore: martial': 0,
      stealth: 0, marksmanship: 0, thievery: 0, driving: 0, dodge: 0, acrobatics: 0, 'lore: survival': 0,
      tracking: 0, education: 0, research: 0, cunning: 0, concentration: 0, investigation: 0, 'lore: scholar': 0,
      willpower: 0, discipline: 0, occult: 0, religion: 0, perception: 0, observation: 0, 'lore: forbidden': 0,
      charm: 0, deception: 0, leadership: 0, intimidation: 0, manipulation: 0, 'lore: social': 0,
      negotiation: 0, finance: 0, assessment: 0, 'street wise': 0, bribe: 0, purchase: 0, 'lore: business': 0,
    });
    setOwnedEquipment([]);
    setOwnedLocations([]);
    setOwnedTraits([]);
    setOwnedTalents([]);
    setNpcBonds([]); // Reset NPC Bonds
    setPcBonds([]);   // Reset PC Bonds
    setCyclesCompleted(0); // Reset Cycles Completed
    setNemeses([]); // Reset Nemeses
    setClues(0); // Reset Clues
    setLeads(0); // Reset Leads
    setAcumen(0); // Reset Acumen
    setEssence(0); // Reset Essence
  };

  // Effect to apply heritage, lifestyle, and background bonuses when selections change
  useEffect(() => {
    // Start with base characteristics
    const baseCharacteristics = {
      physicality: { score: 2, status: 0, defensePool: 0 },
      dexterity: { score: 2, status: 0, defensePool: 0 },
      mentality: { score: 2, status: 0, defensePool: 0 },
      soul: { score: 2, status: 0, defensePool: 0 },
      influence: { score: 2, status: 0, defensePool: 0 },
      affluence: { score: 2, status: 0, defensePool: 0 },
      edge: { score: 2 },
    };

    let updatedCharacteristics = { ...baseCharacteristics };

    // Apply Heritage bonuses
    if (heritage && heritageData[heritage]) {
      const bonuses = heritageData[heritage].bonuses;
      for (const charKey in bonuses) {
        if (updatedCharacteristics[charKey]) {
          updatedCharacteristics = {
            ...updatedCharacteristics,
            [charKey]: {
              ...updatedCharacteristics[charKey],
              score: updatedCharacteristics[charKey].score + bonuses[charKey],
            },
          };
        }
      }
    }

    // Apply Lifestyle bonuses
    if (lifestyle && lifestyleData[lifestyle]) {
      const bonuses = lifestyleData[lifestyle].bonuses;
      for (const charKey in bonuses) {
        if (updatedCharacteristics[charKey]) {
          updatedCharacteristics = {
            ...updatedCharacteristics,
            [charKey]: {
              ...updatedCharacteristics[charKey],
              score: updatedCharacteristics[charKey].score + bonuses[charKey],
            },
          };
        }
      }
    }

    // Apply Background bonuses (currently no direct char bonuses, but structure is here)
    if (background && backgroundData[background]) {
        const bonuses = backgroundData[background].bonuses;
        for (const charKey in bonuses) {
            if (updatedCharacteristics[charKey]) {
                updatedCharacteristics = {
                    ...updatedCharacteristics,
                    [charKey]: {
                        ...updatedCharacteristics[charKey],
                        score: updatedCharacteristics[charKey].score + bonuses[charKey],
                    },
                };
            }
            }
    }

    setCharacteristics(updatedCharacteristics);
  }, [heritage, lifestyle, background]); // Re-run this effect whenever heritage, lifestyle, or background changes


  // Function to calculate die type based on skill rank
  const getDieType = (rank) => {
    switch (parseInt(rank)) {
      case 0: return 'D4'; // Unskilled
      case 1: return 'D6'; // One Rank
      case 2: return 'D8'; // Two Ranks
      case 3: return 'D10'; // Three Ranks
      case 4: return 'D12'; // Four Ranks
      case 5: return '2D8'; // Five Ranks
      case 6: return 'D20'; // Six Ranks
      case 7: return '2D20'; // Seven Ranks
      default: return 'D4'; // Default for invalid ranks
    }
  };

  // Handler for characteristic score changes
  const handleCharacteristicChange = (charName, field, value) => {
    setCharacteristics(prev => ({
      ...prev,
      [charName]: {
        ...prev[charName],
        [field]: parseInt(value) || 0, // Ensure value is a number
      }
    }));
  };

  // Handler for skill rank changes
  const handleSkillChange = (skillName, value) => {
    setSkills(prev => ({
      ...prev,
      [skillName]: parseInt(value) || 0, // Ensure value is a number
    }));
  };

  // Handlers for Owned Equipment
  const handleAddEquipment = () => {
    setOwnedEquipment(prev => [...prev, { id: Date.now(), name: '', type: '', rarity: 1, innateQualities: '', assignedSlot: '', inventoryWildcardsGranted: 0, grantedWildcards: [], qualityTags: [], magicalTags: [] }]);
  };

  const handleRemoveEquipment = (id) => {
    setOwnedEquipment(prev => prev.filter(item => item.id !== id));
  };

  const handleEquipmentChange = (id, field, value) => {
    setOwnedEquipment(prev => prev.map(item => {
      if (item.id === id) {
        let updatedItem = { ...item, [field]: value };

        // Handle inventoryWildcardsGranted slider change
        if (field === 'inventoryWildcardsGranted') {
          const newCount = parseInt(value) || 0;
          const currentWildcards = updatedItem.grantedWildcards || [];
          const newGrantedWildcards = Array.from({ length: newCount }, (_, i) => {
            // Reuse existing wildcard data if available, otherwise create new empty one
            return currentWildcards[i] || { id: Date.now() + Math.random(), text: '', used: false };
          });
          updatedItem.grantedWildcards = newGrantedWildcards;
        }
        // If assignedSlot changes, ensure grantedWildcards is handled correctly
        else if (field === 'assignedSlot') {
          if (!value.startsWith('storage-slot')) {
            updatedItem.grantedWildcards = []; // Clear wildcards if not a storage slot
            updatedItem.inventoryWildcardsGranted = 0; // Reset slider as well
          } else {
            // If it becomes a storage slot, initialize grantedWildcards based on current slider value (or default 0)
            const currentWildcardCount = updatedItem.inventoryWildcardsGranted || 0;
            updatedItem.grantedWildcards = Array.from({ length: currentWildcardCount }, (_, i) => ({
                id: Date.now() + i,
                text: '',
                used: false
            }));
          }
        }
        // If rarity changes, it no longer directly affects grantedWildcards count, but still used for tags
        return updatedItem;
      }
      return item;
    }));
  };

  const handleWildcardDetailChange = (itemId, wildcardId, field, value) => {
    setOwnedEquipment(prevEquipment => prevEquipment.map(item => {
      if (item.id === itemId) {
        return {
          ...item,
          grantedWildcards: item.grantedWildcards.map(wc =>
            wc.id === wildcardId ? { ...wc, [field]: value } : wc
          )
        };
      }
      return item;
    }));
  };

  const handleAddTag = (itemId, tagType, newTag) => {
    if (!newTag.trim()) return; // Don't add empty tags

    setOwnedEquipment(prev => prev.map(item => {
      if (item.id === itemId) {
        const currentTags = item[tagType];
        const maxTags = item.rarity;
        if (currentTags.length < maxTags && !currentTags.includes(newTag.trim())) {
          return { ...item, [tagType]: [...currentTags, newTag.trim()] };
        }
      }
      return item;
    }));

    // Clear the input field after adding
    if (tagType === 'qualityTags') {
      setNewQualityTagInput(prev => ({ ...prev, [itemId]: '' }));
    } else {
      setNewMagicalTagInput(prev => ({ ...prev, [itemId]: '' }));
    }
  };

  const handleRemoveTag = (itemId, tagType, tagToRemove) => {
    setOwnedEquipment(prev => prev.map(item =>
      item.id === itemId ? { ...item, [tagType]: item[tagType].filter(tag => tag !== tagToRemove) } : item
    ));
  };

  // Handlers for Owned Locations
  const handleAddLocation = () => {
    setOwnedLocations(prev => [...prev, { id: Date.now(), name: '', type: '', rarity: 1, isHome: false }]);
  };

  const handleRemoveLocation = (id) => {
    setOwnedLocations(prev => prev.filter(loc => loc.id !== id));
  };

  const handleLocationChange = (id, field, value) => {
    setOwnedLocations(prev => prev.map(loc => {
      if (loc.id === id) {
        // Ensure only one home location can be selected
        if (field === 'isHome' && value === true) {
          // If this location is becoming home, set all others to false
          return { ...loc, [field]: value };
        } else if (field === 'isHome' && value === false) {
          return { ...loc, [field]: value };
        }
        return { ...loc, [field]: value };
      }
      // If another location is becoming home, ensure this one is false
      if (field === 'isHome' && value === true && loc.id !== id) {
        return { ...loc, isHome: false };
      }
      return loc;
    }));
  };

  // Handlers for Traits
  const handleAddTrait = () => {
    setOwnedTraits(prev => [...prev, { id: Date.now(), name: '', class: '', effect: '' }]);
  };

  const handleRemoveTrait = (id) => {
    setOwnedTraits(prev => prev.filter(trait => trait.id !== id));
  };

  const handleTraitChange = (id, field, value) => {
    setOwnedTraits(prev => prev.map(trait =>
      trait.id === id ? { ...trait, [field]: value } : trait
    ));
  };

  // Handlers for Talents
  const handleAddTalent = () => {
    setOwnedTalents(prev => [...prev, { id: Date.now(), type: '', characteristic: '', skill: '', name: '', cost: '', description: '' }]);
  };

  const handleRemoveTalent = (id) => {
    setOwnedTalents(prev => prev.filter(talent => talent.id !== id));
  };

  const handleTalentChange = (id, field, value) => {
    setOwnedTalents(prev => prev.map(talent => {
      if (talent.id === id) {
        let updatedTalent = { ...talent, [field]: value };
        // No longer auto-filling name, cost, description from a data source
        return updatedTalent;
      }
      return talent;
    }));
  };

  // Handlers for NPC Bonds
  const handleAddNpcBond = () => {
    setNpcBonds(prev => [...prev, { id: Date.now(), name: '', description: '' }]);
  };

  const handleRemoveNpcBond = (id) => {
    setNpcBonds(prev => prev.filter(bond => bond.id !== id));
  };

  const handleNpcBondChange = (id, field, value) => {
    setNpcBonds(prev => prev.map(bond =>
      bond.id === id ? { ...bond, [field]: value } : bond
    ));
  };

  // Handlers for PC Bonds
  const handleAddPcBond = () => {
    setPcBonds(prev => [...prev, { id: Date.now(), name: '', description: '' }]);
  };

  const handleRemovePcBond = (id) => {
    setPcBonds(prev => prev.filter(bond => bond.id !== id));
  };

  const handlePcBondChange = (id, field, value) => {
    setPcBonds(prev => prev.map(bond =>
      bond.id === id ? { ...bond, [field]: value } : bond
    ));
  };

  // Handlers for Nemesis
  const handleAddNemesis = () => {
    const newNemesis = {
        id: Date.now(),
        name: 'New Nemesis',
        challengeLevel: 'Easy',
        targetCharacteristic: 'physicality',
        characteristics: { // Initialize with base stats
            physicality: { base: nemesisBaseStats.physicality, modifier: 0, total: nemesisBaseStats.physicality, fate: 'F1' },
            dexterity: { base: nemesisBaseStats.dexterity, modifier: 0, total: nemesisBaseStats.dexterity, fate: 'F1' },
            mentality: { base: nemesisBaseStats.mentality, modifier: 0, total: nemesisBaseStats.mentality, fate: 'F1' },
            soul: { base: nemesisBaseStats.soul, modifier: 0, total: nemesisBaseStats.soul, fate: 'F1' },
            influence: { base: nemesisBaseStats.influence, modifier: 0, total: nemesisBaseStats.influence, fate: 'F1' },
            affluence: { base: nemesisBaseStats.affluence, modifier: 0, total: nemesisBaseStats.affluence, fate: 'F1' },
            edge: { base: nemesisBaseStats.edge, modifier: 0, total: nemesisBaseStats.edge, fate: 'F1' },
        },
        motivation: '',
        specialRules: '',
    };
    // Apply initial boost based on default 'Easy' challenge level
    const initialBoost = nemesisChallengeLevels.find(l => l.value === 'Easy')?.boost || 0;
    if (newNemesis.characteristics[newNemesis.targetCharacteristic]) {
        newNemesis.characteristics[newNemesis.targetCharacteristic].base += initialBoost;
        newNemesis.characteristics[newNemesis.targetCharacteristic].total += initialBoost;
    }
    setNemeses(prev => [...prev, newNemesis]);
  };

  const handleRemoveNemesis = (id) => {
    setNemeses(prev => prev.filter(nemesis => nemesis.id !== id));
  };

  const handleNemesisTextChange = (nemesisId, field, value) => {
    setNemeses(prevNemeses => prevNemeses.map(nemesis =>
        nemesis.id === nemesisId ? { ...nemesis, [field]: value } : nemesis
    ));
  };

  const handleNemesisCharChange = (nemesisId, charKey, field, value) => {
    setNemeses(prevNemeses => prevNemeses.map(nemesis => {
        if (nemesis.id === nemesisId) {
            const updatedNemesis = { ...nemesis };
            const char = updatedNemesis.characteristics[charKey];
            const parsedValue = (field === 'modifier') ? (parseInt(value) || 0) : value; // Parse only if modifier

            updatedNemesis.characteristics = {
                ...updatedNemesis.characteristics,
                [charKey]: {
                    ...char,
                    [field]: parsedValue,
                    // Recalculate total based on updated base and modifier
                    total: (char.base || 0) + (field === 'modifier' ? parsedValue : (char.modifier || 0))
                }
            };
            return updatedNemesis;
        }
        return nemesis;
    }));
  };

  const handleNemesisSelectorChange = (nemesisId, field, value) => {
    setNemeses(prevNemeses => prevNemeses.map(nemesis => {
        if (nemesis.id === nemesisId) {
            const updatedNemesis = { ...nemesis, [field]: value };

            // Recalculate characteristics based on new challenge level or target char
            const selectedChallengeLevel = nemesisChallengeLevels.find(l => l.value === updatedNemesis.challengeLevel);
            const type = selectedChallengeLevel?.label || 'Unknown Type'; // Default if not found
            const boost = selectedChallengeLevel?.boost || 0;
            
            const newCharacteristics = {};
            for (const charKey in nemesisBaseStats) {
                newCharacteristics[charKey] = {
                    base: nemesisBaseStats[charKey],
                    modifier: updatedNemesis.characteristics[charKey]?.modifier || 0,
                    total: nemesisBaseStats[charKey] + (updatedNemesis.characteristics[charKey]?.modifier || 0),
                    fate: updatedNemesis.characteristics[charKey]?.fate || 'F1' // Preserve existing fate or default
                };
            }

            // Apply boost to target characteristic
            if (updatedNemesis.targetCharacteristic && newCharacteristics[updatedNemesis.targetCharacteristic]) {
                newCharacteristics[updatedNemesis.targetCharacteristic] = {
                    ...newCharacteristics[updatedNemesis.targetCharacteristic],
                    base: newCharacteristics[updatedNemesis.targetCharacteristic].base + boost,
                    total: newCharacteristics[updatedNemesis.targetCharacteristic].base + boost + newCharacteristics[updatedNemesis.targetCharacteristic].modifier
                };
            }
            updatedNemesis.characteristics = newCharacteristics;
            updatedNemesis.type = type.split(' ')[1]?.replace('(', '').replace(')', '') || 'Minion'; // Extract type (Minion, Rival, Heroic)
            return updatedNemesis;
        }
        return nemesis;
    }));
  };


  // Function to handle printing to PDF
  const handlePrintToPdf = () => {
    window.print();
  };

  // Function to handle Google Sign-In
  const handleGoogleSignIn = async () => {
    if (!auth) {
      setSaveMessage('Authentication not initialized.');
      return;
    }
    const provider = new GoogleAuthProvider();
    try {
      await signInWithPopup(auth, provider);
      setSaveMessage('Signed in with Google!');
    } catch (error) {
      console.error("Error during Google sign-in:", error);
      setSaveMessage(`Google sign-in failed: ${error.message}`);
    } finally {
      setTimeout(() => setSaveMessage(''), 3000);
    }
  };

  // Function to handle Sign Out
  const handleSignOut = async () => {
    if (!auth) return;
    try {
      await signOut(auth);
      setSaveMessage('Signed out. Data will now be saved anonymously.');
      // After signing out, Firebase will trigger onAuthStateChanged, which will sign in anonymously
    } catch (error) {
      console.error("Error during sign-out:", error);
      setSaveMessage(`Sign out failed: ${error.message}`);
    } finally {
      setTimeout(() => setSaveMessage(''), 3000);
    }
  };

  // Function to save character data to Firestore
  const saveCharacterSheet = async () => {
    if (!db || !userId || !activeCharacterId) {
      setSaveMessage('Error: Not authenticated, database not ready, or no character selected.');
      return;
    }

    setIsSaving(true);
    setSaveMessage('Saving...');

    try {
      const appId = typeof __app_id !== 'undefined' ? __app_id : 'default-app-id';
      const docRef = doc(db, `artifacts/${appId}/users/${userId}/characterSheets`, activeCharacterId);

      const characterData = {
        characterName, // Save the display name
        heritage,
        lifestyle,
        background,
        characterClass,
        motivation, // Save Motivation
        hinderance, // Save Hinderance
        characteristics,
        skills,
        ownedEquipment,
        ownedLocations,
        ownedTraits,
        ownedTalents,
        npcBonds, // Save NPC Bonds
        pcBonds,   // Save PC Bonds
        cyclesCompleted, // Save Cycles Completed
        nemeses, // Save Nemeses
        clues, // Save Clues
        leads, // Save Leads
        acumen, // Save Acumen
        essence, // Save Essence
        lastSaved: new Date().toISOString(), // Add a timestamp
      };

      await setDoc(docRef, characterData, { merge: false }); // Overwrite with current data
      setSaveMessage('Character saved successfully!');

      // Update character list in case name was changed
      setCharacterList(prevList => {
        const existingIndex = prevList.findIndex(char => char.id === activeCharacterId);
        if (existingIndex > -1) {
          const newList = [...prevList];
          newList[existingIndex] = { id: activeCharacterId, name: characterName };
          return newList;
        }
        return prevList;
      });

    } catch (error) {
      console.error("Error saving document:", error);
      setSaveMessage(`Save failed: ${error.message}`);
    } finally {
      setIsSaving(false);
      setTimeout(() => setSaveMessage(''), 3000); // Clear message after 3 seconds
    }
  };

  // Function to create a new character
  const createNewCharacter = async () => {
    const newName = prompt("Enter a unique name for your new character:");
    if (!newName || newName.trim() === '') {
      setSaveMessage('Character name cannot be empty.');
      setTimeout(() => setSaveMessage(''), 3000);
      return;
    }
    const newId = newName.trim().replace(/\s+/g, '-').toLowerCase(); // Create a simple ID from name

    // Check if character already exists
    if (characterList.some(char => char.id === newId)) {
      setSaveMessage('A character with this name already exists. Please choose a different name or load the existing one.');
      setTimeout(() => setSaveMessage(''), 5000);
      return;
    }

    // Reset all states to default for a new character
    resetCharacterSheet();
    setCharacterName(newName.trim()); // Set the display name
    setActiveCharacterId(newId); // Set the new character as active

    // Add to character list immediately
    setCharacterList(prevList => [...prevList, { id: newId, name: newName.trim() }]);

    setSaveMessage(`New character "${newName.trim()}" created. Remember to save!`);
    setTimeout(() => setSaveMessage(''), 3000);
  };

  // Function to delete the currently active character
  const deleteCurrentCharacter = async () => {
    if (!db || !userId || !activeCharacterId) {
      setSaveMessage('No character selected to delete.');
      setTimeout(() => setSaveMessage(''), 3000);
      return;
    }

    const confirmDelete = window.confirm(`Are you sure you want to delete "${characterName}"? This action cannot be undone.`);
    if (!confirmDelete) return;

    setIsSaving(true);
    setSaveMessage('Deleting character...');

    try {
      const appId = typeof __app_id !== 'undefined' ? __app_id : 'default-app-id';
      const docRef = doc(db, `artifacts/${appId}/users/${userId}/characterSheets`, activeCharacterId);
      await deleteDoc(docRef);

      setSaveMessage(`Character "${characterName}" deleted successfully.`);
      
      // Remove from character list
      setCharacterList(prevList => prevList.filter(char => char.id !== activeCharacterId));

      // Reset to a blank sheet or load another character if available
      if (characterList.length > 1) { // If there were other characters
        const remainingChar = characterList.find(char => char.id !== activeCharacterId);
        setActiveCharacterId(remainingChar.id);
        setCharacterName(remainingChar.name);
        loadCharacterData(remainingChar.id);
      } else { // No more characters left
        resetCharacterSheet();
        setActiveCharacterId('');
        setCharacterName('');
        setIsLoadingData(false); // Ensure loading state is false after reset
      }

    } catch (error) {
      console.error("Error deleting document:", error);
      setSaveMessage(`Delete failed: ${error.message}`);
    } finally {
      setIsSaving(false);
      setTimeout(() => setSaveMessage(''), 3000);
    }
  };


  if (isLoadingData && !activeCharacterId && characterList.length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 to-gray-700 text-gray-100 flex items-center justify-center">
        <div className="text-2xl font-bold text-blue-400">Loading Character Sheet...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 to-gray-700 text-gray-100 p-6 sm:p-8 font-inter antialiased">
      <div className="max-w-4xl mx-auto bg-gray-800 rounded-xl shadow-2xl p-6 sm:p-10 border border-gray-700">
        <h1 className="text-4xl sm:text-5xl font-extrabold text-center text-blue-500 mb-8 tracking-tight">
          Mistfall: Armageddon
        </h1>
        <h2 className="text-2xl sm:text-3xl font-bold text-center text-blue-400 mb-6 border-b border-blue-700 pb-3">
          Character Sheet
        </h2>

        {/* User ID and Auth Status Display */}
        <div className="text-center text-sm text-gray-400 mb-4 p-2 bg-gray-700 rounded-md border border-gray-600">
          {userEmail ? (
            <p>Signed in as: <span className="font-mono text-blue-300 break-all">{userEmail}</span></p>
          ) : (
            <p>Your User ID: <span className="font-mono text-blue-300 break-all">{userId}</span></p>
          )}
          <p className="text-xs mt-1">
            {userEmail ? 'Your data is saved to your Google account.' : 'Your data is saved anonymously. Clear browser data to reset ID.'}
          </p>
          <div className="flex justify-center gap-4 mt-2">
            {!userEmail && ( // Only show Google sign-in if not already signed in with Google
              <button
                onClick={handleGoogleSignIn}
                className="bg-blue-500 hover:bg-blue-600 text-white font-bold py-1 px-3 rounded-md text-xs transition duration-300 ease-in-out"
              >
                Sign in with Google
              </button>
            )}
            {userEmail && ( // Only show sign out if signed in with Google
              <button
                onClick={handleSignOut}
                className="bg-gray-600 hover:bg-gray-700 text-white font-bold py-1 px-3 rounded-md text-xs transition duration-300 ease-in-out"
              >
                Sign Out
              </button>
            )}
          </div>
        </div>

        {/* Character Management Section */}
        <section className="mb-8 p-4 bg-gray-700 rounded-lg shadow-inner border border-gray-600">
          <h3 className="text-xl sm:text-2xl font-semibold text-blue-300 mb-4">Character Management</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label htmlFor="characterSelector" className="block text-sm font-medium text-gray-300 mb-1">Select Character:</label>
              <select
                id="characterSelector"
                value={activeCharacterId}
                onChange={(e) => setActiveCharacterId(e.target.value)}
                className="w-full p-2 bg-gray-900 border border-gray-600 rounded-md text-gray-100 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">{characterList.length > 0 ? "Load a character" : "New Character"}</option>
                {characterList.map(char => (
                  <option key={char.id} value={char.id}>{char.name}</option>
                ))}
              </select>
            </div>
            <div className="flex flex-col gap-2">
              <button
                onClick={createNewCharacter}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-md transition duration-300 ease-in-out shadow-lg transform hover:scale-105"
              >
                New Character
              </button>
              <button
                onClick={deleteCurrentCharacter}
                className="w-full bg-red-600 hover:bg-red-700 text-white font-bold py-2 px-4 rounded-md transition duration-300 ease-in-out shadow-lg transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={!activeCharacterId}
              >
                Delete Current Character
              </button>
            </div>
          </div>
          {saveMessage && (
            <p className="text-center mt-2 text-sm font-medium text-blue-300">{saveMessage}</p>
          )}
        </section>


        {/* Character Identity Section */}
        <section className="mb-8 p-4 bg-gray-700 rounded-lg shadow-inner border border-gray-600">
          <h3 className="text-xl sm:text-2xl font-semibold text-blue-300 mb-4">Character Identity</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Reordered fields */}
            <div>
              <label htmlFor="characterNameInput" className="block text-sm font-medium text-gray-300 mb-1">Character Name:</label>
              <input
                type="text"
                id="characterNameInput"
                value={characterName}
                onChange={(e) => setCharacterName(e.target.value)}
                className="w-full p-2 bg-gray-900 border border-gray-600 rounded-md text-gray-100 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Enter Name"
              />
            </div>
            <div>
              <label htmlFor="heritage" className="block text-sm font-medium text-gray-300 mb-1">Heritage:</label>
              <select
                id="heritage"
                value={heritage}
                onChange={(e) => setHeritage(e.target.value)}
                className="w-full p-2 bg-gray-900 border border-gray-600 rounded-md text-gray-100 focus:ring-blue-500 focus:border-blue-500"
              >
                {Object.entries(heritageData).map(([key, data]) => (
                  <option key={key} value={key}>{data.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="lifestyle" className="block text-sm font-medium text-gray-300 mb-1">Lifestyle:</label>
              <select
                id="lifestyle"
                value={lifestyle}
                onChange={(e) => setLifestyle(e.target.value)}
                className="w-full p-2 bg-gray-900 border border-gray-600 rounded-md text-gray-100 focus:ring-blue-500 focus:border-blue-500"
              >
                {Object.entries(lifestyleData).map(([key, data]) => (
                  <option key={key} value={key}>{data.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="background" className="block text-sm font-medium text-gray-300 mb-1">Background:</label>
              <select
                id="background"
                value={background}
                onChange={(e) => setBackground(e.target.value)}
                className="w-full p-2 bg-gray-900 border border-gray-600 rounded-md text-gray-100 focus:ring-blue-500 focus:border-blue-500"
              >
                {Object.entries(backgroundData).map(([key, data]) => (
                  <option key={key} value={key}>{data.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="characterClass" className="block text-sm font-medium text-gray-300 mb-1">Class:</label>
              <select
                id="characterClass"
                value={characterClass}
                onChange={(e) => setCharacterClass(e.target.value)}
                className="w-full p-2 bg-gray-900 border border-gray-600 rounded-md text-gray-100 focus:ring-blue-500 focus:border-blue-500"
              >
                {Object.entries(classData).map(([key, data]) => (
                  <option key={key} value={key}>{data.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="motivation" className="block text-sm font-medium text-gray-300 mb-1">Motivation:</label>
              <select
                id="motivation"
                value={motivation}
                onChange={(e) => setMotivation(e.target.value)}
                className="w-full p-2 bg-gray-900 border border-gray-600 rounded-md text-gray-100 focus:ring-blue-500 focus:border-blue-500"
              >
                {Object.entries(motivationData).map(([key, data]) => (
                  <option key={key} value={key}>{data.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="hinderance" className="block text-sm font-medium text-gray-300 mb-1">Hinderance:</label>
              <select
                id="hinderance"
                value={hinderance}
                onChange={(e) => setHinderance(e.target.value)}
                className="w-full p-2 bg-gray-900 border border-gray-600 rounded-md text-gray-100 focus:ring-blue-500 focus:border-blue-500"
              >
                {Object.entries(hinderanceData).map(([key, data]) => (
                  <option key={key} value={key}>{data.label}</option>
                ))}
              </select>
            </div>
          </div>
        </section>

        {/* Characteristics Section */}
        <section className="mb-8 p-4 bg-gray-700 rounded-lg shadow-inner border border-gray-600">
          <h3 className="text-xl sm:text-2xl font-semibold text-blue-300 mb-4">Characteristics</h3>
          <p className="text-sm text-gray-400 mb-4">(Your innate abilities. Each starts at 2 unless modified by Heritage/Lifestyle)</p>
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-gray-900">
                  <th className="p-3 border border-gray-600 font-bold text-blue-200 uppercase text-sm rounded-tl-lg">Characteristic</th>
                  <th className="p-3 border border-gray-600 font-bold text-blue-200 uppercase text-sm">Score</th>
                  <th className="p-3 border border-gray-600 font-bold text-blue-200 uppercase text-sm">Status Pool (Current / Max)</th>
                  <th className="p-3 border border-gray-600 font-bold text-blue-200 uppercase text-sm rounded-tr-lg">Defense Pool</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(characteristics).map(([key, char]) => (
                  <tr key={key} className="even:bg-gray-750 odd:bg-gray-700">
                    <td className="p-3 border border-gray-600 capitalize font-medium">{key}</td>
                    <td className="p-3 border border-gray-600">
                      <input
                        type="number"
                        min="0"
                        value={char.score}
                        onChange={(e) => handleCharacteristicChange(key, 'score', e.target.value)}
                        className="w-16 p-1 bg-gray-900 border border-gray-600 rounded-md text-gray-100 text-center focus:ring-blue-500 focus:border-blue-500"
                      />
                    </td>
                    <td className="p-3 border border-gray-600">
                      {key !== 'edge' ? (
                        <div className="flex items-center space-x-2">
                          <input
                            type="number"
                            value={char.status}
                            onChange={(e) => handleCharacteristicChange(key, 'status', e.target.value)}
                            className="w-16 p-1 bg-gray-900 border border-gray-600 rounded-md text-gray-100 text-center focus:ring-blue-500 focus:border-blue-500"
                          />
                          <span className="text-gray-300">/ {char.score}</span>
                        </div>
                      ) : (
                        <span className="text-gray-500 italic">N/A</span>
                      )}
                    </td>
                    <td className="p-3 border border-gray-600">
                      {key !== 'edge' ? ( // Edge doesn't have a defense pool
                        <input
                          type="number"
                          min="0"
                          value={char.defensePool}
                          onChange={(e) => handleCharacteristicChange(key, 'defensePool', e.target.value)}
                          className="w-16 p-1 bg-gray-900 border border-gray-600 rounded-md text-gray-100 text-center focus:ring-blue-500 focus:border-blue-500"
                        />
                      ) : (
                        <span className="text-gray-500 italic">N/A</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* Skills Section */}
        <section className="mb-8 p-4 bg-gray-700 rounded-lg shadow-inner border border-gray-600">
          <h3 className="text-xl sm:text-2xl font-semibold text-blue-300 mb-4">Skills</h3>
          <p className="text-sm text-gray-400 mb-4">(Learned abilities that complement Characteristics. Untrained skills use a D4. Fill in the rank to see die type.)</p>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {Object.entries(skillCategories).map(([category, skillNames]) => (
              <div key={category} className="mb-6 last:mb-0 col-span-full">
                <h4 className="text-lg sm:text-xl font-medium text-blue-200 mb-3 border-b border-gray-600 pb-2">
                  {category.charAt(0).toUpperCase() + category.slice(1)} Skills
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {skillNames.map(skillName => (
                    <div key={skillName} className="flex flex-col items-start space-y-1">
                      <label htmlFor={`skill-${skillName}`} className="text-sm font-medium text-gray-300 w-full truncate capitalize">
                        {skillName.split(': ').map(s => s.charAt(0).toUpperCase() + s.slice(1)).join(': ')}:
                      </label>
                      <input
                        type="range"
                        min="0"
                        max="7"
                        id={`skill-${skillName}`}
                        value={skills[skillName]}
                        onChange={(e) => handleSkillChange(skillName, e.target.value)}
                        className="w-full h-2 bg-gray-600 rounded-lg appearance-none cursor-pointer range-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50"
                      />
                      <div className="text-center text-gray-300 text-sm mt-1">
                        Rank: {skills[skillName]} (Die Type: {getDieType(skills[skillName])})
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Currency & Resources Section */}
        <section className="mt-8 p-4 bg-gray-700 rounded-lg shadow-inner border border-gray-600">
          <h3 className="text-xl sm:text-2xl font-semibold text-blue-300 mb-4">Currency & Resources 💰</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label htmlFor="clues" className="block text-sm font-medium text-gray-300 mb-1">Clues:</label>
              <input
                type="number"
                min="0"
                id="clues"
                value={clues}
                onChange={(e) => setClues(parseInt(e.target.value) || 0)}
                className="w-full p-2 bg-gray-900 border border-gray-600 rounded-md text-gray-100 text-center focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label htmlFor="leads" className="block text-sm font-medium text-gray-300 mb-1">Leads:</label>
              <input
                type="number"
                min="0"
                id="leads"
                value={leads}
                onChange={(e) => setLeads(parseInt(e.target.value) || 0)}
                className="w-full p-2 bg-gray-900 border border-gray-600 rounded-md text-gray-100 text-center focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label htmlFor="acumen" className="block text-sm font-medium text-gray-300 mb-1">Acumen:</label>
              <input
                type="number"
                min="0"
                id="acumen"
                value={acumen}
                onChange={(e) => setAcumen(parseInt(e.target.value) || 0)}
                className="w-full p-2 bg-gray-900 border border-gray-600 rounded-md text-gray-100 text-center focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label htmlFor="essence" className="block text-sm font-medium text-gray-300 mb-1">Essence:</label>
              <input
                type="number"
                min="0"
                id="essence"
                value={essence}
                onChange={(e) => setEssence(parseInt(e.target.value) || 0)}
                className="w-full p-2 bg-gray-900 border border-gray-600 rounded-md text-gray-100 text-center focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
        </section>

        {/* Traits Section */}
        <section className="mt-8 p-4 bg-gray-700 rounded-lg shadow-inner border border-gray-600">
          <h3 className="text-xl sm:text-2xl font-semibold text-blue-300 mb-4">Traits ✨</h3>
          <p className="text-sm text-gray-400 mb-4">Innate abilities or mystically bestowed aspects of your character.</p>
          <button
            onClick={handleAddTrait}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-md transition duration-300 ease-in-out shadow-lg transform hover:scale-105 mb-4"
          >
            Add New Trait
          </button>
          <div className="space-y-4">
            {ownedTraits.map(trait => (
              <div key={trait.id} className="bg-gray-800 p-4 rounded-lg border border-gray-600 flex flex-col gap-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor={`trait-name-${trait.id}`} className="block text-sm font-medium text-gray-300 mb-1">Name:</label>
                    <input
                      type="text"
                      id={`trait-name-${trait.id}`}
                      value={trait.name}
                      onChange={(e) => handleTraitChange(trait.id, 'name', e.target.value)}
                      className="w-full p-2 bg-gray-900 border border-gray-600 rounded-md text-gray-100 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="e.g., Stone-skin"
                    />
                  </div>
                  <div>
                    <label htmlFor={`trait-class-${trait.id}`} className="block text-sm font-medium text-gray-300 mb-1">Class:</label>
                    <select
                      id={`trait-class-${trait.id}`}
                      value={trait.class}
                      onChange={(e) => handleTraitChange(trait.id, 'class', e.target.value)}
                      className="w-full p-2 bg-gray-900 border border-gray-600 rounded-md text-gray-100 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">Select Class</option>
                      <option value="Physical">Physical</option>
                      <option value="Elemental">Elemental</option>
                      <option value="Otherworldly">Otherworldly</option>
                      <option value="Divine">Divine</option>
                      <option value="Infernal">Infernal</option>
                      <option value="Astral">Astral</option>
                      <option value="Chaotic">Chaotic</option>
                    </select>
                  </div>
                </div>
                {/* New Effect field */}
                <div>
                  <label htmlFor={`trait-effect-${trait.id}`} className="block text-sm font-medium text-gray-300 mb-1">Effect:</label>
                  <textarea
                    id={`trait-effect-${trait.id}`}
                    value={trait.effect}
                    onChange={(e) => handleTraitChange(trait.id, 'effect', e.target.value)}
                    className="w-full p-2 bg-gray-900 border border-gray-600 rounded-md text-gray-100 focus:ring-blue-500 focus:border-blue-500 h-20 resize-y"
                    placeholder="Describe the trait's effect..."
                  ></textarea>
                </div>
                <button
                  onClick={() => handleRemoveTrait(trait.id)}
                  className="bg-blue-700 hover:bg-blue-800 text-white font-bold py-2 px-4 rounded-md transition duration-300 ease-in-out shadow-lg transform hover:scale-105 self-end sm:self-center"
                >
                  Remove
                </button>
              </div>
            ))}
            {ownedTraits.length === 0 && (
              <p className="text-gray-400 text-center py-4">No traits added yet. Click "Add New Trait" to get started!</p>
            )}
          </div>
        </section>

        {/* Talents Section */}
        <section className="mt-8 p-4 bg-gray-700 rounded-lg shadow-inner border border-gray-600">
          <h3 className="text-xl sm:text-2xl font-semibold text-blue-300 mb-4">Talents 💡</h3>
          <p className="text-sm text-gray-400 mb-4">Specialized abilities gained through focused training or unique circumstances.</p>
          <button
            onClick={handleAddTalent}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-md transition duration-300 ease-in-out shadow-lg transform hover:scale-105 mb-4"
          >
            Add New Talent
          </button>
          <div className="space-y-4">
            {ownedTalents.map(talent => (
              <div key={talent.id} className="bg-gray-800 p-4 rounded-lg border border-gray-600 flex flex-col gap-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Type Selector */}
                  <div>
                    <label htmlFor={`talent-type-${talent.id}`} className="block text-sm font-medium text-gray-300 mb-1">Type:</label>
                    <select
                      id={`talent-type-${talent.id}`}
                      value={talent.type}
                      onChange={(e) => handleTalentChange(talent.id, 'type', e.target.value)}
                      className="w-full p-2 bg-gray-900 border border-gray-600 rounded-md text-gray-100 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">Select Type</option>
                      {['Mundane', 'Magical'].map(typeKey => (
                        <option key={typeKey} value={typeKey}>{typeKey}</option>
                      ))}
                    </select>
                  </div>

                  {/* Characteristic Text Input */}
                  <div>
                    <label htmlFor={`talent-char-${talent.id}`} className="block text-sm font-medium text-gray-300 mb-1">Characteristic:</label>
                    <input
                      type="text"
                      id={`talent-char-${talent.id}`}
                      value={talent.characteristic}
                      onChange={(e) => handleTalentChange(talent.id, 'characteristic', e.target.value)}
                      className="w-full p-2 bg-gray-900 border border-gray-600 rounded-md text-gray-100 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="e.g., physicality, soul"
                    />
                  </div>

                  {/* Skill/Manifestation Text Input */}
                  <div>
                    <label htmlFor={`talent-skill-${talent.id}`} className="block text-sm font-medium text-gray-300 mb-1">Skill/Manifestation:</label>
                    <input
                      type="text"
                      id={`talent-skill-${talent.id}`}
                      value={talent.skill}
                      onChange={(e) => handleTalentChange(talent.id, 'skill', e.target.value)}
                      className="w-full p-2 bg-gray-900 border border-gray-600 rounded-md text-gray-100 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="e.g., Brawling, Manifestation: Fire"
                    />
                  </div>

                  {/* Talent Name Text Input */}
                  <div>
                    <label htmlFor={`talent-name-${talent.id}`} className="block text-sm font-medium text-gray-300 mb-1">Talent Name:</label>
                    <input
                      type="text"
                      id={`talent-name-${talent.id}`}
                      value={talent.name}
                      onChange={(e) => handleTalentChange(talent.id, 'name', e.target.value)}
                      className="w-full p-2 bg-gray-900 border border-gray-600 rounded-md text-gray-100 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="e.g., Iron Fist"
                    />
                  </div>

                  {/* Cost Text Input */}
                  <div>
                    <label htmlFor={`talent-cost-${talent.id}`} className="block text-sm font-medium text-gray-300 mb-1">Cost (Acumen/Essence):</label>
                    <input
                      type="text"
                      id={`talent-cost-${talent.id}`}
                      value={talent.cost}
                      onChange={(e) => handleTalentChange(talent.id, 'cost', e.target.value)}
                      className="w-full p-2 bg-gray-900 border border-gray-600 rounded-md text-gray-100 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="e.g., 3 Acumen, 5 Essence"
                    />
                  </div>
                </div>

                {/* Description Textarea */}
                <div className="mt-2">
                  <label htmlFor={`talent-description-${talent.id}`} className="block text-sm font-medium text-gray-300 mb-1">Description:</label>
                  <textarea
                    id={`talent-description-${talent.id}`}
                    value={talent.description}
                    onChange={(e) => handleTalentChange(talent.id, 'description', e.target.value)}
                    className="w-full p-2 bg-gray-900 border border-gray-600 rounded-md text-gray-100 focus:ring-blue-500 focus:border-blue-500 h-20 resize-y"
                    placeholder="Enter the talent's full description or rules..."
                  ></textarea>
                </div>

                <button
                  onClick={() => handleRemoveTalent(talent.id)}
                  className="bg-blue-700 hover:bg-blue-800 text-white font-bold py-2 px-4 rounded-md transition duration-300 ease-in-out shadow-lg transform hover:scale-105 self-end mt-4"
                >
                  Remove Talent
                </button>
              </div>
            ))}
            {ownedTalents.length === 0 && (
              <p className="text-gray-400 text-center py-4">No talents added yet. Click "Add New Talent" to get started!</p>
            )}
          </div>
        </section>

        {/* Owned Equipment Section */}
        <section className="mt-8 p-4 bg-gray-700 rounded-lg shadow-inner border border-gray-600">
          <h3 className="text-xl sm:text-2xl font-semibold text-blue-300 mb-4">Owned Equipment 🛠️</h3>
          <p className="text-sm text-gray-400 mb-4">Track your gear, its type, rarity, and available slots.</p>
          <button
            onClick={handleAddEquipment}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-md transition duration-300 ease-in-out shadow-lg transform hover:scale-105 mb-4"
          >
            Add New Equipment
          </button>
          <div className="space-y-4">
            {ownedEquipment.map(item => (
              <div key={item.id} className="bg-gray-800 p-4 rounded-lg border border-gray-600 flex flex-col gap-4">
                {/* Equipment Details */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div>
                    <label htmlFor={`equipment-name-${item.id}`} className="block text-sm font-medium text-gray-300 mb-1">Name:</label>
                    <input
                      type="text"
                      id={`equipment-name-${item.id}`}
                      value={item.name}
                      onChange={(e) => handleEquipmentChange(item.id, 'name', e.target.value)}
                      className="w-full p-2 bg-gray-900 border border-gray-600 rounded-md text-gray-100 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="e.g., Demon Blade"
                    />
                  </div>
                  <div>
                    <label htmlFor={`equipment-type-${item.id}`} className="block text-sm font-medium text-gray-300 mb-1">Type:</label>
                    <input
                      type="text"
                      id={`equipment-type-${item.id}`}
                      value={item.type}
                      onChange={(e) => handleEquipmentChange(item.id, 'type', e.target.value)}
                      className="w-full p-2 bg-gray-900 border border-gray-600 rounded-md text-gray-100 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="e.g., Melee Weapon, Attire"
                    />
                  </div>
                  <div>
                    <label htmlFor={`equipment-rarity-${item.id}`} className="block text-sm font-medium text-gray-300 mb-1">Rarity:</label>
                    <input
                      type="number"
                      min="1"
                      id={`equipment-rarity-${item.id}`}
                      value={item.rarity}
                      onChange={(e) => handleEquipmentChange(item.id, 'rarity', parseInt(e.target.value) || 1)}
                      className="w-full p-2 bg-gray-900 border border-gray-600 rounded-md text-gray-100 text-center focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <label className="block text-sm font-medium text-gray-300">Quality Slots:</label>
                    <span className="p-2 bg-gray-900 border border-gray-600 rounded-md text-gray-100 text-center w-16">
                      {item.rarity}
                    </span>
                    <label className="block text-sm font-medium text-gray-300">Magical Slots:</label>
                    <span className="p-2 bg-gray-900 border border-gray-600 rounded-md text-gray-100 text-center w-16">
                      {item.rarity}
                    </span>
                  </div>
                   <div>
                    <label htmlFor={`equipment-slot-${item.id}`} className="block text-sm font-medium text-gray-300 mb-1">Assigned Slot:</label>
                    <select
                      id={`equipment-slot-${item.id}`}
                      value={item.assignedSlot}
                      onChange={(e) => handleEquipmentChange(item.id, 'assignedSlot', e.target.value)}
                      className="w-full p-2 bg-gray-900 border border-gray-600 rounded-md text-gray-100 focus:ring-blue-500 focus:border-blue-500"
                    >
                      {inventorySlots.map(slot => (
                        <option key={slot.value} value={slot.value}>{slot.label}</option>
                      ))}
                    </select>
                  </div>
                </div>
                {/* Innate Qualities field */}
                <div className="mt-2">
                  <label htmlFor={`equipment-innate-${item.id}`} className="block text-sm font-medium text-gray-300 mb-1">Innate Qualities:</label>
                  <textarea
                    id={`equipment-innate-${item.id}`}
                    value={item.innateQualities}
                    onChange={(e) => handleEquipmentChange(item.id, 'innateQualities', e.target.value)}
                    className="w-full p-2 bg-gray-900 border border-gray-600 rounded-md text-gray-100 focus:ring-blue-500 focus:border-blue-500 h-20 resize-y"
                    placeholder="Describe any inherent qualities of this item..."
                  ></textarea>
                </div>

                {/* Conditional Inventory Wildcards Granted section */}
                {item.assignedSlot.startsWith('storage-slot') && (
                  <div className="mt-2 border-t border-gray-600 pt-4">
                    <h4 className="text-md font-semibold text-blue-200 mb-2">Inventory Wildcards</h4>
                    <div className="mb-3">
                      <label htmlFor={`inventory-wildcards-count-${item.id}`} className="block text-sm font-medium text-gray-300 mb-1">Number of Wildcards:</label>
                      <input
                        type="range"
                        min="0"
                        max="10"
                        id={`inventory-wildcards-count-${item.id}`}
                        value={item.inventoryWildcardsGranted || 0}
                        onChange={(e) => handleEquipmentChange(item.id, 'inventoryWildcardsGranted', parseInt(e.target.value) || 0)}
                        className="w-full h-2 bg-gray-600 rounded-lg appearance-none cursor-pointer range-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50"
                      />
                      <div className="text-center text-gray-300 text-sm mt-1">{item.inventoryWildcardsGranted || 0}</div>
                    </div>
                    <div className="space-y-2">
                      {Array.from({ length: item.inventoryWildcardsGranted || 0 }).map((_, index) => {
                        // Ensure wildcard object exists before accessing its properties
                        const wildcard = item.grantedWildcards[index] || { id: Date.now() + index, text: '', used: false };
                        return (
                          <div key={wildcard.id} className="flex items-center gap-2 bg-gray-750 p-2 rounded-md">
                            <input
                              type="text"
                              value={wildcard.text}
                              onChange={(e) => handleWildcardDetailChange(item.id, wildcard.id, 'text', e.target.value)}
                              className="flex-grow p-1 bg-gray-900 border border-gray-600 rounded-md text-gray-100 text-sm focus:ring-blue-500 focus:border-blue-500"
                              placeholder={`Wildcard ${index + 1} Description`}
                            />
                            <input
                              type="checkbox"
                              checked={wildcard.used}
                              onChange={(e) => handleWildcardDetailChange(item.id, wildcard.id, 'used', e.target.checked)}
                              className="h-4 w-4 text-blue-600 rounded border-gray-600 focus:ring-blue-500"
                            />
                            <label className="text-sm text-gray-300">Used</label>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Quality Tags Section */}
                <div className="border-t border-gray-600 pt-4 mt-4">
                  <h4 className="text-md font-semibold text-blue-200 mb-2">Quality Tags ({item.qualityTags.length}/{item.rarity})</h4>
                  <div className="flex flex-wrap gap-2 mb-3">
                    {item.qualityTags.map((tag, index) => (
                      <span key={index} className="bg-gray-900 text-gray-100 px-3 py-1 rounded-full text-sm flex items-center gap-1">
                        {tag}
                        <button
                          onClick={() => handleRemoveTag(item.id, 'qualityTags', tag)}
                          className="text-blue-400 hover:text-blue-200 text-xs font-bold"
                        >
                          &times;
                        </button>
                      </span>
                    ))}
                  </div>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={newQualityTagInput[item.id] || ''}
                      onChange={(e) => setNewQualityTagInput(prev => ({ ...prev, [item.id]: e.target.value }))}
                      className="flex-grow p-2 bg-gray-900 border border-gray-600 rounded-md text-gray-100 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Add quality tag"
                    />
                    <button
                      onClick={() => handleAddTag(item.id, 'qualityTags', newQualityTagInput[item.id])}
                      className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-md transition duration-300 ease-in-out disabled:opacity-50 disabled:cursor-not-allowed"
                      disabled={item.qualityTags.length >= item.rarity || !newQualityTagInput[item.id]}
                    >
                      Add
                    </button>
                  </div>
                </div>

                {/* Magical Tags Section */}
                <div className="border-t border-gray-600 pt-4 mt-4">
                  <h4 className="text-md font-semibold text-blue-200 mb-2">Magical Tags ({item.magicalTags.length}/{item.rarity})</h4>
                  <div className="flex flex-wrap gap-2 mb-3">
                    {item.magicalTags.map((tag, index) => (
                      <span key={index} className="bg-gray-900 text-gray-100 px-3 py-1 rounded-full text-sm flex items-center gap-1">
                        {tag}
                        <button
                          onClick={() => handleRemoveTag(item.id, 'magicalTags', tag)}
                          className="text-blue-400 hover:text-blue-200 text-xs font-bold"
                        >
                          &times;
                        </button>
                      </span>
                    ))}
                  </div>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={newMagicalTagInput[item.id] || ''}
                      onChange={(e) => setNewMagicalTagInput(prev => ({ ...prev, [item.id]: e.target.value }))}
                      className="flex-grow p-2 bg-gray-900 border border-gray-600 rounded-md text-gray-100 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Add magical tag"
                    />
                    <button
                      onClick={() => handleAddTag(item.id, 'magicalTags', newMagicalTagInput[item.id])}
                      className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-md transition duration-300 ease-in-out disabled:opacity-50 disabled:cursor-not-allowed"
                      disabled={item.magicalTags.length >= item.rarity || !newMagicalTagInput[item.id]}
                    >
                      Add
                    </button>
                  </div>
                </div>

                <button
                  onClick={() => handleRemoveEquipment(item.id)}
                  className="bg-blue-700 hover:bg-blue-800 text-white font-bold py-2 px-4 rounded-md transition duration-300 ease-in-out shadow-lg transform hover:scale-105 self-end mt-4"
                >
                  Remove Equipment
                </button>
              </div>
            ))}
            {ownedEquipment.length === 0 && (
              <p className="text-gray-400 text-center py-4">No equipment added yet. Click "Add New Equipment" to get started!</p>
            )}
          </div>
        </section>

        {/* Owned Locations Section */}
        <section className="mt-8 p-4 bg-gray-700 rounded-lg shadow-inner border border-gray-600">
          <h3 className="text-xl sm:text-2xl font-semibold text-blue-300 mb-4">Owned Locations 🏠</h3>
          <p className="text-sm text-gray-400 mb-4">Keep track of your character's properties and safe havens.</p>
          <button
            onClick={handleAddLocation}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-md transition duration-300 ease-in-out shadow-lg transform hover:scale-105 mb-4"
          >
            Add New Location
          </button>
          <div className="space-y-4">
            {ownedLocations.map(loc => (
              <div key={loc.id} className="bg-gray-800 p-4 rounded-lg border border-gray-600 flex flex-col sm:flex-row sm:items-center gap-4">
                <div className="flex-grow grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor={`location-name-${loc.id}`} className="block text-sm font-medium text-gray-300 mb-1">Name:</label>
                    <input
                      type="text"
                      id={`location-name-${loc.id}`}
                      value={loc.name}
                      onChange={(e) => handleLocationChange(loc.id, 'name', e.target.value)}
                      className="w-full p-2 bg-gray-900 border border-gray-600 rounded-md text-gray-100 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="e.g., Heart's Hearth"
                    />
                  </div>
                  <div>
                    <label htmlFor={`location-type-${loc.id}`} className="block text-sm font-medium text-gray-300 mb-1">Type:</label>
                    <input
                      type="text"
                      id={`location-type-${loc.id}`}
                      value={loc.type}
                      onChange={(e) => handleLocationChange(loc.id, 'type', e.target.value)}
                      className="w-full p-2 bg-gray-900 border border-gray-600 rounded-md text-gray-100 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="e.g., Bar/Pub, Apartment"
                    />
                  </div>
                  <div>
                    <label htmlFor={`location-rarity-${loc.id}`} className="block text-sm font-medium text-gray-300 mb-1">Rarity:</label>
                    <input
                      type="number"
                      min="1"
                      id={`location-rarity-${loc.id}`}
                      value={loc.rarity}
                      onChange={(e) => handleLocationChange(loc.id, 'rarity', parseInt(e.target.value) || 1)}
                      className="w-full p-2 bg-gray-900 border border-gray-600 rounded-md text-gray-100 text-center focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id={`location-home-${loc.id}`}
                      checked={loc.isHome}
                      onChange={(e) => handleLocationChange(loc.id, 'isHome', e.target.checked)}
                      className="h-5 w-5 text-blue-600 rounded border-gray-600 focus:ring-blue-500"
                    />
                    <label htmlFor={`location-home-${loc.id}`} className="ml-2 block text-sm font-medium text-gray-300">Is Home Location?</label>
                  </div>
                </div>
                <button
                  onClick={() => handleRemoveLocation(loc.id)}
                  className="bg-blue-700 hover:bg-blue-800 text-white font-bold py-2 px-4 rounded-md transition duration-300 ease-in-out shadow-lg transform hover:scale-105 self-end sm:self-center"
                >
                  Remove
                </button>
              </div>
            ))}
            {ownedLocations.length === 0 && (
              <p className="text-gray-400 text-center py-4">No locations added yet. Click "Add New Location" to get started!</p>
            )}
          </div>
        </section>

        {/* NPC Bonds Section */}
        <section className="mt-8 p-4 bg-gray-700 rounded-lg shadow-inner border border-gray-600">
          <h3 className="text-xl sm:text-2xl font-semibold text-blue-300 mb-4">NPC Bonds 🤝</h3>
          <p className="text-sm text-gray-400 mb-4">Relationships with Non-Player Characters.</p>
          <button
            onClick={handleAddNpcBond}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-md transition duration-300 ease-in-out shadow-lg transform hover:scale-105 mb-4"
          >
            Add New NPC Bond
          </button>
          <div className="space-y-4">
            {npcBonds.map(bond => (
              <div key={bond.id} className="bg-gray-800 p-4 rounded-lg border border-gray-600 flex flex-col gap-4">
                <div>
                  <label htmlFor={`npc-bond-name-${bond.id}`} className="block text-sm font-medium text-gray-300 mb-1">Name:</label>
                  <input
                    type="text"
                    id={`npc-bond-name-${bond.id}`}
                    value={bond.name}
                    onChange={(e) => handleNpcBondChange(bond.id, 'name', e.target.value)}
                    className="w-full p-2 bg-gray-900 border border-gray-600 rounded-md text-gray-100 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="e.g., Old Man Tiberius"
                  />
                </div>
                <div>
                  <label htmlFor={`npc-bond-description-${bond.id}`} className="block text-sm font-medium text-gray-300 mb-1">Description:</label>
                  <textarea
                    id={`npc-bond-description-${bond.id}`}
                    value={bond.description}
                    onChange={(e) => handleNpcBondChange(bond.id, 'description', e.target.value)}
                    className="w-full p-2 bg-gray-900 border border-gray-600 rounded-md text-gray-100 focus:ring-blue-500 focus:border-blue-500 h-20 resize-y"
                    placeholder="Describe the nature of this bond..."
                  ></textarea>
                </div>
                <button
                  onClick={() => handleRemoveNpcBond(bond.id)}
                  className="bg-blue-700 hover:bg-blue-800 text-white font-bold py-2 px-4 rounded-md transition duration-300 ease-in-out shadow-lg transform hover:scale-105 self-end"
                >
                  Remove
                </button>
              </div>
            ))}
            {npcBonds.length === 0 && (
              <p className="text-gray-400 text-center py-4">No NPC bonds added yet. Click "Add New NPC Bond" to get started!</p>
            )}
          </div>
        </section>

        {/* PC Bonds Section */}
        <section className="mt-8 p-4 bg-gray-700 rounded-lg shadow-inner border border-gray-600">
          <h3 className="text-xl sm:text-2xl font-semibold text-blue-300 mb-4">PC Bonds 🤝</h3>
          <p className="text-sm text-gray-400 mb-4">Relationships with other Player Characters.</p>
          <button
            onClick={handleAddPcBond}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-md transition duration-300 ease-in-out shadow-lg transform hover:scale-105 mb-4"
          >
            Add New PC Bond
          </button>
          <div className="space-y-4">
            {pcBonds.map(bond => (
              <div key={bond.id} className="bg-gray-800 p-4 rounded-lg border border-gray-600 flex flex-col gap-4">
                <div>
                  <label htmlFor={`pc-bond-name-${bond.id}`} className="block text-sm font-medium text-gray-300 mb-1">Name:</label>
                  <input
                    type="text"
                    id={`pc-bond-name-${bond.id}`}
                    value={bond.name}
                    onChange={(e) => handlePcBondChange(bond.id, 'name', e.target.value)}
                    className="w-full p-2 bg-gray-900 border border-gray-600 rounded-md text-gray-100 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="e.g., Sarah (The Rogue)"
                  />
                </div>
                <div>
                  <label htmlFor={`pc-bond-description-${bond.id}`} className="block text-sm font-medium text-gray-300 mb-1">Description:</label>
                  <textarea
                    id={`pc-bond-description-${bond.id}`}
                    value={bond.description}
                    onChange={(e) => handlePcBondChange(bond.id, 'description', e.target.value)}
                    className="w-full p-2 bg-gray-900 border border-gray-600 rounded-md text-gray-100 focus:ring-blue-500 focus:border-blue-500 h-20 resize-y"
                    placeholder="Describe the nature of this bond..."
                  ></textarea>
                </div>
                <button
                  onClick={() => handleRemovePcBond(bond.id)}
                  className="bg-blue-700 hover:bg-blue-800 text-white font-bold py-2 px-4 rounded-md transition duration-300 ease-in-out shadow-lg transform hover:scale-105 self-end"
                >
                  Remove
                </button>
              </div>
            ))}
            {pcBonds.length === 0 && (
              <p className="text-gray-400 text-center py-4">No PC bonds added yet. Click "Add New PC Bond" to get started!</p>
            )}
          </div>
        </section>

        {/* Nemesis Section */}
        <section className="mt-8 p-4 bg-gray-700 rounded-lg shadow-inner border border-gray-600">
          <h3 className="text-xl sm:text-2xl font-semibold text-blue-300 mb-4">Nemesis 😈</h3>
          <p className="text-sm text-gray-400 mb-4">Track your character's recurring adversaries.</p>
          <div className="mb-4">
            <label htmlFor="cyclesCompleted" className="block text-sm font-medium text-gray-300 mb-1">Cycles Completed:</label>
            <input
              type="number"
              min="0"
              id="cyclesCompleted"
              value={cyclesCompleted}
              onChange={(e) => setCyclesCompleted(parseInt(e.target.value) || 0)}
              className="w-24 p-2 bg-gray-900 border border-gray-600 rounded-md text-gray-100 text-center focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <button
            onClick={handleAddNemesis}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-md transition duration-300 ease-in-out shadow-lg transform hover:scale-105 mb-4"
          >
            Add New Nemesis
          </button>
          <div className="space-y-6">
            {nemeses.map(nemesis => (
              <div key={nemesis.id} className="bg-gray-800 p-4 rounded-lg border border-gray-600 flex flex-col gap-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor={`nemesis-name-${nemesis.id}`} className="block text-sm font-medium text-gray-300 mb-1">Nemesis Name:</label>
                    <input
                      type="text"
                      id={`nemesis-name-${nemesis.id}`}
                      value={nemesis.name}
                      onChange={(e) => handleNemesisTextChange(nemesis.id, 'name', e.target.value)}
                      className="w-full p-2 bg-gray-900 border border-gray-600 rounded-md text-gray-100 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Enter Nemesis Name"
                    />
                  </div>
                  <div>
                    <label htmlFor={`nemesis-challenge-${nemesis.id}`} className="block text-sm font-medium text-gray-300 mb-1">Challenge Level:</label>
                    <select
                      id={`nemesis-challenge-${nemesis.id}`}
                      value={nemesis.challengeLevel}
                      onChange={(e) => handleNemesisSelectorChange(nemesis.id, 'challengeLevel', e.target.value)}
                      className="w-full p-2 bg-gray-900 border border-gray-600 rounded-md text-gray-100 focus:ring-blue-500 focus:border-blue-500 capitalize"
                    >
                      {nemesisChallengeLevels.map(level => (
                        <option key={level.value} value={level.value}>{level.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label htmlFor={`nemesis-target-char-${nemesis.id}`} className="block text-sm font-medium text-gray-300 mb-1">Target Characteristic:</label>
                    <select
                      id={`nemesis-target-char-${nemesis.id}`}
                      value={nemesis.targetCharacteristic}
                      onChange={(e) => handleNemesisSelectorChange(nemesis.id, 'targetCharacteristic', e.target.value)}
                      className="w-full p-2 bg-gray-900 border border-gray-600 rounded-md text-gray-100 focus:ring-blue-500 focus:border-blue-500 capitalize"
                    >
                      {nemesisCharacteristicTypes.map(charType => (
                        <option key={charType.value} value={charType.value}>{charType.label}</option>
                      ))}
                    </select>
                  </div>
                  <div className="col-span-full">
                    <label htmlFor={`nemesis-motivation-${nemesis.id}`} className="block text-sm font-medium text-gray-300 mb-1">Motivation:</label>
                    <textarea
                      id={`nemesis-motivation-${nemesis.id}`}
                      value={nemesis.motivation}
                      onChange={(e) => handleNemesisTextChange(nemesis.id, 'motivation', e.target.value)}
                      className="w-full p-2 bg-gray-900 border border-gray-600 rounded-md text-gray-100 focus:ring-blue-500 focus:border-blue-500 h-20 resize-y"
                      placeholder="Nemesis's motivation..."
                    ></textarea>
                  </div>
                </div>

                <h5 className="text-md font-semibold text-blue-200 mb-2">Characteristics:</h5>
                <div className="overflow-x-auto">
                  <table className="w-full text-left border-collapse">
                    <thead>
                      <tr className="bg-gray-900">
                        <th className="p-2 border border-gray-600 font-bold text-blue-200 uppercase text-xs">Char</th>
                        <th className="p-2 border border-gray-600 font-bold text-blue-200 uppercase text-xs">Base</th>
                        <th className="p-2 border border-gray-600 font-bold text-blue-200 uppercase text-xs">Modifier</th>
                        <th className="p-2 border border-gray-600 font-bold text-blue-200 uppercase text-xs">Total</th>
                        <th className="p-2 border border-gray-600 font-bold text-blue-200 uppercase text-xs">Fate</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(nemesis.characteristics).map(([charKey, charStats]) => (
                        <tr key={charKey} className="even:bg-gray-750 odd:bg-gray-700">
                          <td className="p-2 border border-gray-600 capitalize text-sm">{charKey}</td>
                          <td className="p-2 border border-gray-600 text-sm">{charStats.base}</td>
                          <td className="p-2 border border-gray-600">
                            <input
                              type="number"
                              value={charStats.modifier}
                              onChange={(e) => handleNemesisCharChange(nemesis.id, charKey, 'modifier', e.target.value)}
                              className="w-16 p-1 bg-gray-900 border border-gray-600 rounded-md text-gray-100 text-center text-sm focus:ring-blue-500 focus:border-blue-500"
                            />
                          </td>
                          <td className="p-2 border border-gray-600 text-sm">{charStats.total}</td>
                          <td className="p-2 border border-gray-600">
                            <select
                              value={charStats.fate}
                              onChange={(e) => handleNemesisCharChange(nemesis.id, charKey, 'fate', e.target.value)}
                              className="w-16 p-1 bg-gray-900 border border-gray-600 rounded-md text-gray-100 text-center text-sm focus:ring-blue-500 focus:border-blue-500"
                            >
                              {fateValues.map(fate => (
                                <option key={fate} value={fate}>{fate}</option>
                              ))}
                            </select>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="col-span-full">
                    <label htmlFor={`nemesis-special-rules-${nemesis.id}`} className="block text-sm font-medium text-gray-300 mb-1">Special Rules:</label>
                    <textarea
                      id={`nemesis-special-rules-${nemesis.id}`}
                      value={nemesis.specialRules}
                      onChange={(e) => handleNemesisTextChange(nemesis.id, 'specialRules', e.target.value)}
                      className="w-full p-2 bg-gray-900 border border-gray-600 rounded-md text-gray-100 focus:ring-blue-500 focus:border-blue-500 h-20 resize-y"
                      placeholder="List special rules or abilities, e.g., 'Flight', 'Regeneration (2 Vitality/round)'"
                    ></textarea>
                  </div>

                <button
                  onClick={() => handleRemoveNemesis(nemesis.id)}
                  className="bg-blue-700 hover:bg-blue-800 text-white font-bold py-2 px-4 rounded-md transition duration-300 ease-in-out shadow-lg transform hover:scale-105 self-end"
                >
                  Remove Nemesis
                </button>
              </div>
            ))}
            {nemeses.length === 0 && (
              <p className="text-gray-400 text-center py-4">No nemeses added yet. Click "Add New Nemesis" to get started!</p>
            )}
          </div>
        </section>

        {/* Save and Print Buttons */}
        <div className="mt-12 text-center flex flex-col sm:flex-row justify-center gap-4">
          <button
            onClick={saveCharacterSheet}
            className="bg-green-600 hover:bg-green-700 text-white font-bold py-3 px-8 rounded-lg transition duration-300 ease-in-out shadow-xl transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={isSaving || !isAuthReady || !db || !activeCharacterId}
          >
            {isSaving ? 'Saving...' : 'Save Character 💾'}
          </button>
          <button
            onClick={handlePrintToPdf}
            className="bg-blue-800 hover:bg-blue-900 text-white font-bold py-3 px-8 rounded-lg transition duration-300 ease-in-out shadow-xl transform hover:scale-105"
          >
            Print Character Sheet to PDF 🖨️
          </button>
        </div>
        {saveMessage && (
          <p className="text-center mt-4 text-sm font-medium text-blue-300">{saveMessage}</p>
        )}
      </div>
    </div>
  );
};

export default App;
